import { inject, Injectable, OnDestroy } from '@angular/core';
import { Client, JSONValue, User, UserUpdateReason } from '@twilio/conversations';
import { Observable, Subject } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { equalSets, subjectsComplete } from 'src/app/common/app.helpers';
import { equalUserInfos, UserInfo } from 'src/app/interfaces';
import { ConversationsAdapter } from '../../helpers/conversations.adapter';
import { ChatEngineOperations } from '../../interfaces/chat-engine/chat-engine-error.service';
import { ChatEngineLifeСycleService } from '../../interfaces/chat-engine/chat-engine-lifecycle-service';
import { ChatEngineHandle, ChatTraceData } from '../../interfaces/chat-model.interface';
import { ChatBlockingService } from '../chat-blocking.service';
import { TwilioChatEngineHandle } from './twilio-chat-engine.handle';
import { TwilioClientAccessService } from './twilio-client-access-service';
import { TwilioErrorService, TwilioInternalOperations } from './twilio-error.service';
import { TwilioUserAttributes, TwilioUserUpdated } from './twilio.interfaces';

interface CurrentUserState {
  user: User;
  userInfo: UserInfo;
  blockedUserIds: Set<string>;
}

@Injectable()
export class TwilioUsersService implements OnDestroy {
  public get currentUserInitialized(): Observable<void> {
    return this.currentUserInitialized$.asObservable();
  }

  public get currentUserInfoUpdated(): Observable<UserInfo> {
    return this.currentUserInfoUpdated$.asObservable();
  }

  public get otherUserInfoUpdated(): Observable<UserInfo> {
    return this.otherUserInfoUpdated$.asObservable();
  }

  public get blockedUsersListChanged(): Observable<void> {
    return this.blockedUsersListChanged$.asObservable();
  }

  private currentUserState: CurrentUserState | null;
  private otherSubscribedUsers: Map<string, UserInfo> = new Map();

  private currentUserInitialized$: Subject<void> = new Subject();
  private currentUserInfoUpdated$: Subject<UserInfo> = new Subject();
  private otherUserInfoUpdated$: Subject<UserInfo> = new Subject();
  private blockedUsersListChanged$: Subject<void> = new Subject();

  private chatBlockingService = inject(ChatBlockingService);
  private chatEngineLifeCycleService = inject(ChatEngineLifeСycleService);
  private twilioClientAccessService = inject(TwilioClientAccessService);
  private twilioErrorService = inject(TwilioErrorService);
  private sessionService = inject(SessionService);

  public constructor() {
    this.subscibeToEngineReadinessChanged();
  }

  public ngOnDestroy(): void {
    subjectsComplete([
      this.currentUserInitialized$,
      this.otherUserInfoUpdated$,
      this.currentUserInfoUpdated$,
      this.blockedUsersListChanged$,
    ]);
  }

  public get currentUser(): User | null {
    return this.currentUserState ? this.currentUserState.user : null;
  }

  public get currentUserAttributes(): TwilioUserAttributes | null {
    if (!this.currentUserState) {
      return null;
    }
    return this.currentUserState.user.attributes as TwilioUserAttributes;
  }

  public async updateCurrentUserAttributes(attributes: TwilioUserAttributes): Promise<void> {
    if (this.currentUserState) {
      this.currentUserState.user = await this.currentUserState.user.updateAttributes(attributes as JSONValue);
    } else {
      return Promise.reject('No current user');
    }
  }

  private subscibeToEngineReadinessChanged(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.chatEngineLifeCycleService.engineReadinessChanged.subscribe(async (engineHandle: ChatEngineHandle) => {
      if (engineHandle.isValid()) {
        await this.initTwilioUser(engineHandle as TwilioChatEngineHandle);
      } else {
        this.currentUserState = null;
        this.otherSubscribedUsers.clear();
      }
    });
  }

  private async initTwilioUser(engineHandle: TwilioChatEngineHandle): Promise<void> {
    try {
      const user: User = await engineHandle.client.getUser(this.sessionService.userId);
      if (engineHandle.isValid()) {
        this.currentUserState = this.buildUserState(user);
        this.currentUserInitialized$.next();

        this.subscribeToChatBlockingEvents();
        this.subscribeToUserEvents();
      }
    } catch (error) {
      this.twilioErrorService.onTwilioInternalOperationFailed(
        TwilioInternalOperations.USER_INITIALIZATION,
        error,
        {} as ChatTraceData,
      );
      this.currentUserState = null;
    }
  }

  private buildUserState(user: User): CurrentUserState {
    const userAttributes: TwilioUserAttributes = user.attributes as TwilioUserAttributes;
    const blockedUserIds: string[] = (userAttributes.blocked || []).sort();

    return {
      user,
      userInfo: ConversationsAdapter.adaptTwilioUserToUserInfo(user, blockedUserIds),
      blockedUserIds: new Set(blockedUserIds),
    };
  }

  private subscribeToChatBlockingEvents(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.chatBlockingService.userBlocked.subscribe(async (blockedUser: UserInfo) => {
      try {
        await this.blockUser(blockedUser);
        this.blockedUsersListChanged$.next();
      } catch (error) {
        this.twilioErrorService.onOperationFailed(ChatEngineOperations.BLOCK_USER, error, {
          userId: blockedUser.userId,
        } as ChatTraceData);
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.chatBlockingService.userUnblocked.subscribe(async (unblockedUser: UserInfo) => {
      try {
        await this.unblockUser(unblockedUser);
        this.blockedUsersListChanged$.next();
      } catch (error) {
        this.twilioErrorService.onOperationFailed(ChatEngineOperations.UNBLOCK_USER, error, {
          userId: unblockedUser.userId,
        } as ChatTraceData);
      }
    });
  }

  private async blockUser(targetUser: UserInfo): Promise<void> {
    let needsTwilioUpdate = false;

    const userAttributes: TwilioUserAttributes = this.currentUserAttributes || ({} as TwilioUserAttributes);
    if (!userAttributes.blocked) {
      userAttributes.blocked = [targetUser.userId];
      needsTwilioUpdate = true;
    } else if (userAttributes.blocked.indexOf(targetUser.userId) === -1) {
      userAttributes.blocked.push(targetUser.userId);
      needsTwilioUpdate = true;
    }

    if (needsTwilioUpdate) {
      this.currentUserState.blockedUserIds.add(targetUser.userId);
      await this.updateCurrentUserAttributes(userAttributes);
    }
  }

  private async unblockUser(targetUser: UserInfo): Promise<void> {
    const userAttributes: TwilioUserAttributes | null = this.currentUserAttributes;
    if (!userAttributes || !userAttributes.blocked) {
      return;
    }

    const userIndex: number = userAttributes.blocked.indexOf(targetUser.userId);
    if (userIndex === -1) {
      return;
    }

    userAttributes.blocked.splice(userIndex, 1);
    if (userAttributes.blocked.length === 0) {
      userAttributes.blocked = undefined;
    }

    this.currentUserState.blockedUserIds.delete(targetUser.userId);
    await this.updateCurrentUserAttributes(userAttributes);
  }

  private subscribeToUserEvents(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('userSubscribed', (user: User) => {
      this.handleUserSubscribed(user);
    });

    twilioClient.on('userUnsubscribed', (user: User) => {
      this.handleUserUnsubscribed(user);
    });

    twilioClient.on('userUpdated', (event: TwilioUserUpdated) => {
      if (this.isUserUpdateWithSatisfyingReason(event.updateReasons)) {
        this.handleUserUpdated(event);
      }
    });
  }

  private handleUserSubscribed(user: User): void {
    const existingUserInfo: UserInfo | undefined = this.otherSubscribedUsers.get(user.identity);

    if (!existingUserInfo) {
      const newUserInfo: UserInfo = ConversationsAdapter.adaptTwilioUserToUserInfo(
        user,
        Array.from(this.currentUserState.blockedUserIds),
      );

      this.otherSubscribedUsers.set(user.identity, newUserInfo);
      this.onOtherUserUpdated(newUserInfo);
    }
  }

  private handleUserUnsubscribed(user: User): void {
    const existingUserInfo: UserInfo | undefined = this.otherSubscribedUsers.get(user.identity);
    if (existingUserInfo) {
      this.otherSubscribedUsers.delete(user.identity);
    }
  }

  private isUserUpdateWithSatisfyingReason(reasons: UserUpdateReason[]): boolean {
    return reasons.some((reason: UserUpdateReason) => reason === 'friendlyName' || reason === 'attributes');
  }

  private handleUserUpdated(event: TwilioUserUpdated): void {
    // Current user?
    if (this.currentUserState && event.user.identity === this.currentUserState.user.identity) {
      const previousUserState: CurrentUserState = { ...this.currentUserState };
      this.currentUserState = this.buildUserState(event.user);
      this.onCurrentUserUpdated(previousUserState, this.currentUserState);
    } else {
      // Was user fully subscribed by now?
      // If not, this might be a partial update from Twilio that we would skip
      // (such as friendlyName or attributes separately),
      const existingUserInfo: UserInfo | undefined = this.otherSubscribedUsers.get(event.user.identity);
      if (!existingUserInfo) {
        return;
      }
      const modifiedName: boolean = event.updateReasons.indexOf('friendlyName') !== -1;
      const modifiedAttributes: boolean = event.updateReasons.indexOf('attributes') !== -1;

      let updatedUserInfo: UserInfo;
      if (modifiedName && modifiedAttributes) {
        updatedUserInfo = ConversationsAdapter.adaptTwilioUserToUserInfo(
          event.user,
          Array.from(this.currentUserState.blockedUserIds),
        );
      } else if (modifiedName && !modifiedAttributes) {
        updatedUserInfo = { ...existingUserInfo, userName: event.user.friendlyName };
      } else if (modifiedAttributes && !modifiedName) {
        updatedUserInfo = {
          ...ConversationsAdapter.adaptTwilioUserToUserInfo(
            event.user,
            Array.from(this.currentUserState.blockedUserIds),
          ),
          userName: existingUserInfo.userName,
        };
      } else {
        throw new Error('Either friendlyName or attributes should have been modified');
      }

      if (!equalUserInfos(existingUserInfo, updatedUserInfo)) {
        this.otherSubscribedUsers.set(event.user.identity, updatedUserInfo);
        this.onOtherUserUpdated(updatedUserInfo);
      }
    }
  }

  private onCurrentUserUpdated(previousUserState: CurrentUserState, newUserState: CurrentUserState): void {
    // Session data sync
    if (this.sessionService.user) {
      this.sessionService.user.image_url = newUserState.userInfo.userImage;
      this.sessionService.user.displayed_name = newUserState.userInfo.userName;
    }

    // Main user info update event
    this.currentUserInfoUpdated$.next(newUserState.userInfo);

    // Detect changes in blocked users list
    if (!equalSets(previousUserState.blockedUserIds, newUserState.blockedUserIds)) {
      this.blockedUsersListChanged$.next();

      // It's safe to trigger. When we are doing local block/unblock actions,
      // the user state is immediately synchronized, so the comparison above should not detect  a difference
      this.chatBlockingService.onBlockedUserListChangedExternally();
    }

    // TODO muted channels too
  }

  private onOtherUserUpdated(updatedUserInfo: UserInfo): void {
    this.otherUserInfoUpdated$.next(updatedUserInfo);
  }
}
